import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { RefreshCw, ArrowRight } from 'lucide-react';
import { api } from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';

/**
 * ONBOARD-01 — Starter-only usage counter.
 *
 * Replaces the generic UsageMeter for users with `plan_type === 'starter'`.
 * Emphasises the remaining FREE consultations rather than the burn-down bar,
 * and surfaces a graduated upgrade CTA as the user approaches the limit.
 *
 * Color tiers:
 *   13–15 remaining → teal  (default)
 *    6–12 remaining → amber ("Aproveite seu período grátis")
 *    1–5  remaining → orange ("Últimas consultas grátis!")
 *    0    remaining → red   ("Limite atingido")
 */
export const StarterUsageCounter = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await api.getUsageStats();
        if (mounted) setUsage(data);
      } catch {
        if (mounted) setUsage(null);
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (!usage) {
    return (
      <div
        className={`rounded-xl border p-5 animate-pulse ${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}
        data-testid="starter-counter-loading"
      >
        <div className="h-7 w-24 bg-slate-300 dark:bg-slate-700 rounded" />
        <div className="h-3 w-40 bg-slate-200 dark:bg-slate-700 rounded mt-3" />
      </div>
    );
  }

  const used = usage.consultations_used || 0;
  const included = usage.consultations_included || 15;
  const remaining = Math.max(0, included - used);

  // Tier semantics: PROMPT spec — 13-15 teal, 6-12 amber, 1-5 orange, 0 red.
  let tier = 'teal';
  let tierMessage = null;
  if (remaining === 0) {
    tier = 'red';
    tierMessage = 'Limite atingido';
  } else if (remaining <= 5) {
    tier = 'orange';
    tierMessage = 'Últimas consultas grátis!';
  } else if (remaining <= 12) {
    tier = 'amber';
    tierMessage = 'Aproveite seu período grátis';
  }

  const tierStyles = {
    teal: {
      border: 'border-l-teal-500',
      number: 'text-teal-600 dark:text-teal-400',
      message: '',
    },
    amber: {
      border: 'border-l-amber-500',
      number: 'text-amber-600 dark:text-amber-400',
      message: 'text-amber-700 dark:text-amber-300',
    },
    orange: {
      border: 'border-l-orange-500',
      number: 'text-orange-600 dark:text-orange-400',
      message: 'text-orange-700 dark:text-orange-300',
    },
    red: {
      border: 'border-l-red-500',
      number: 'text-red-600 dark:text-red-400',
      message: 'text-red-700 dark:text-red-300 font-medium',
    },
  };
  const s = tierStyles[tier];

  // Upgrade CTA shows from "6 or fewer remaining" per spec.
  const showUpgradeCta = remaining <= 6;

  const resetDate = usage.reset_date ? new Date(usage.reset_date) : null;
  const resetLabel = resetDate
    ? resetDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : null;

  const cardBg = isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200';

  return (
    <div
      className={`rounded-xl border-l-4 border ${s.border} ${cardBg} p-5`}
      data-testid="starter-usage-counter"
      data-tier={tier}
    >
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className={`text-3xl font-bold leading-tight ${s.number}`} data-testid="starter-remaining">
            {remaining} grátis
          </div>
          <div className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            consultas restantes este mês
          </div>
          {tierMessage && (
            <div className={`text-xs mt-1 ${s.message}`} data-testid="starter-tier-message">
              {tierMessage}
            </div>
          )}
        </div>
        {resetLabel && (
          <div className={`text-xs flex items-center gap-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} data-testid="starter-reset">
            <RefreshCw className="w-3 h-3" /> Renova em {resetLabel}
          </div>
        )}
      </div>

      {showUpgradeCta && (
        <div className="mt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/upgrade?source=starter_counter')}
            className="border-orange-300 text-orange-700 hover:bg-orange-50 dark:border-orange-700 dark:text-orange-300 dark:hover:bg-orange-950/30"
            data-testid="starter-upgrade-cta"
          >
            Conhecer planos pagos
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default StarterUsageCounter;
